// Photo Upload Configuration
const API_BASE_URL = 'https://shopify-store-omega.vercel.app';

class PhotoUpload {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.sectionId;
    this.orderId = null;
    this.selectedFile = null;
    this.isUploading = false;

    // Elements
    this.formContainer = this.container.querySelector(`#upload-container-${this.sectionId}`);
    this.successContainer = this.container.querySelector(`#success-container-${this.sectionId}`);
    this.errorContainer = this.container.querySelector(`#error-container-${this.sectionId}`);
    
    this.dropArea = this.container.querySelector(`#drop-area-${this.sectionId}`);
    this.fileInput = this.container.querySelector(`#file-input-${this.sectionId}`);
    this.previewContainer = this.container.querySelector(`#preview-container-${this.sectionId}`);
    this.previewImg = this.container.querySelector(`#photo-preview-${this.sectionId}`);
    this.removeBtn = this.container.querySelector(`#remove-photo-${this.sectionId}`);
    this.submitBtn = this.container.querySelector(`#submit-upload-${this.sectionId}`);
    this.statusIndicator = this.container.querySelector(`#upload-status-${this.sectionId}`);
    
    this.orderBadge = this.container.querySelector(`#order-badge-${this.sectionId}`);
    this.displayOrderId = this.container.querySelector(`#display-order-id-${this.sectionId}`);
    this.successOrderId = this.container.querySelector(`#success-order-id-${this.sectionId}`);

    this.init();
  }

  init() {
    this.extractOrderId();
    this.setupEventListeners();
  }

  extractOrderId() {
    const urlParams = new URLSearchParams(window.location.search);
    this.orderId = urlParams.get('order_id') || urlParams.get('orderid') || urlParams.get('order');
    
    if (this.orderId) {
      if (this.orderBadge) this.orderBadge.style.display = 'inline-flex';
      if (this.displayOrderId) this.displayOrderId.textContent = this.orderId;
      console.log('Extracted Order ID:', this.orderId);
    } else {
      console.warn('No Order ID found in URL. Users should ideally have an order_id.');
      // We still allow it but maybe alert them
    }
  }

  setupEventListeners() {
    // File Selection
    this.fileInput.addEventListener('change', (e) => this.handleFileSelect(e));

    // Drag and Drop
    ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
      this.dropArea.addEventListener(eventName, (e) => {
        e.preventDefault();
        e.stopPropagation();
      }, false);
    });

    this.dropArea.addEventListener('dragover', () => this.dropArea.classList.add('drag-over'));
    this.dropArea.addEventListener('dragleave', () => this.dropArea.classList.remove('drag-over'));
    this.dropArea.addEventListener('drop', (e) => {
      this.dropArea.classList.remove('drag-over');
      const files = e.dataTransfer.files;
      if (files.length) {
        this.fileInput.files = files;
        this.handleFileSelect();
      }
    });

    // Remove Photo
    this.removeBtn.addEventListener('click', () => this.resetFile());

    // Submit
    this.submitBtn.addEventListener('click', () => this.uploadPhoto());

    // Retry
    const retryBtn = this.container.querySelector(`#retry-upload-${this.sectionId}`);
    if (retryBtn) {
      retryBtn.addEventListener('click', () => {
        this.errorContainer.style.display = 'none';
        this.formContainer.style.display = 'block';
      });
    }

    // Upload Another
    const anotherBtn = this.container.querySelector(`#upload-another-${this.sectionId}`);
    if (anotherBtn) {
      anotherBtn.addEventListener('click', () => {
        this.resetFile();
        this.successContainer.style.display = 'none';
        this.formContainer.style.display = 'block';
      });
    }
  }

  handleFileSelect() {
    const file = this.fileInput.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file.');
      this.fileInput.value = '';
      return;
    }

    this.selectedFile = file;
    
    // Show Preview
    const reader = new FileReader();
    reader.onload = (e) => {
      this.previewImg.src = e.target.result;
      this.dropArea.style.display = 'none';
      this.previewContainer.style.display = 'block';
      this.submitBtn.disabled = false;
    };
    reader.readAsDataURL(file);
  }

  resetFile() {
    this.selectedFile = null;
    this.fileInput.value = '';
    this.previewImg.src = '';
    this.previewContainer.style.display = 'none';
    this.dropArea.style.display = 'block';
    this.submitBtn.disabled = true;
    this.statusIndicator.style.display = 'none';
  }

  async uploadPhoto() {
    if (!this.selectedFile || this.isUploading) return;

    if (!this.orderId) {
      const manualId = prompt("Please enter your Order ID to continue:");
      if (!manualId) return;
      this.orderId = manualId;
      if (this.displayOrderId) this.displayOrderId.textContent = this.orderId;
    }

    this.isUploading = true;
    this.submitBtn.disabled = true;
    
    // Update button text to uploading
    this._originalBtnText = this.submitBtn.innerHTML;
    this.submitBtn.innerHTML = 'Uploading...';
    
    this.statusIndicator.style.display = 'flex';

    try {
      // 1. Compress image client-side to stay under Vercel's 4.5MB body limit
      const base64Photo = await this.compressImage(this.selectedFile);

      // 2. Prepare Payload
      const payload = {
        order_id: this.orderId,
        photo: base64Photo,
        fileName: this.selectedFile.name,
        contentType: 'image/jpeg', // always JPEG after compression
        sessionId: this.getSessionId(),
        metadata: {
          pageUrl: window.location.href,
          originalViewport: `${window.innerWidth}x${window.innerHeight}`
        }
      };

      // 3. Send to API
      const response = await fetch(`${API_BASE_URL}/api/upload/photo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Upload failed');
      }

      console.log('✅ Upload Success:', result);
      this.showSuccess();

    } catch (error) {
      console.error('❌ Upload Error:', error);
      this.showError(error.message);
    } finally {
      this.isUploading = false;
      this.statusIndicator.style.display = 'none';
      
      // Restore button text if it didn't succeed (if it succeeded, the form is hidden anyway, but safe to restore)
      if (this._originalBtnText) {
        this.submitBtn.innerHTML = this._originalBtnText;
      }
    }
  }

  compressImage(file, maxDimension = 1200, quality = 0.75) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onerror = error => reject(error);
      reader.onload = (e) => {
        const img = new Image();
        img.onerror = reject;
        img.onload = () => {
          // Calculate new dimensions keeping aspect ratio
          let { width, height } = img;
          if (width > maxDimension || height > maxDimension) {
            if (width > height) {
              height = Math.round((height * maxDimension) / width);
              width = maxDimension;
            } else {
              width = Math.round((width * maxDimension) / height);
              height = maxDimension;
            }
          }

          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          // Always output as JPEG for consistent compression
          const compressed = canvas.toDataURL('image/jpeg', quality);
          console.log(`📦 Compressed: ${(compressed.length * 0.75 / 1024).toFixed(0)} KB`);
          resolve(compressed);
        };
        img.src = e.target.result;
      };
    });
  }

  getSessionId() {
    let sessionId = localStorage.getItem('ratings_session_id');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('ratings_session_id', sessionId);
    }
    return sessionId;
  }

  showSuccess() {
    this.formContainer.style.display = 'none';
    if (this.successOrderId) this.successOrderId.textContent = this.orderId;
    this.successContainer.style.display = 'block';
    this.successContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  showError(message) {
    this.formContainer.style.display = 'none';
    const msgEl = this.container.querySelector(`#error-message-${this.sectionId}`);
    if (msgEl) msgEl.textContent = message;
    this.errorContainer.style.display = 'block';
  }
}

// Initialization
document.addEventListener("DOMContentLoaded", () => {
  const containers = document.querySelectorAll('.photo-upload-page');
  containers.forEach(container => {
    if (!container.dataset.initialized) {
      container.dataset.initialized = 'true';
      new PhotoUpload(container);
    }
  });
});

// Shopify Section Load
document.addEventListener("shopify:section:load", (event) => {
  const container = event.target.querySelector('.photo-upload-page');
  if (container && !container.dataset.initialized) {
    container.dataset.initialized = 'true';
    new PhotoUpload(container);
  }
});
